import { useMemo } from "react";

const SERIES = [
  { key: "rent", color: "#2563eb" },
  { key: "house", color: "#0f766e" },
  { key: "apartment", color: "#7c3aed" },
  { key: "farm", color: "#c26a16" },
];

const WIDTH = 760;
const HEIGHT = 300;
const PADDING = { top: 18, right: 18, bottom: 40, left: 18 };

function validAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function formatDate(date, locale) {
  try {
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(
      new Date(`${date}T12:00:00`)
    );
  } catch {
    return date;
  }
}

export default function CasaMapaMedianPriceTrendChart({
  points = [],
  locale,
  formatMoney,
  t,
}) {
  const availableSeries = useMemo(
    () =>
      SERIES.map((series) => ({
        ...series,
        values: points
          .map((point, index) => ({
            index,
            date: point.date,
            value: validAmount(point[series.key]),
            sampleSize: point[`${series.key}SampleSize`] || 0,
          }))
          .filter((point) => point.value != null),
      })),
    [points]
  );

  const allValues = availableSeries.flatMap((series) =>
    series.values.map((point) => point.value)
  );
  if (!points.length || !allValues.length) {
    return (
      <p className="cm-muted cm-market-trend__empty">
        {t("realEstate.casaMapa.marketIntelligence.trend.empty")}
      </p>
    );
  }

  const minLog = Math.log10(Math.min(...allValues));
  const maxLog = Math.log10(Math.max(...allValues));
  const logRange = Math.max(maxLog - minLog, 0.2);
  const innerWidth = WIDTH - PADDING.left - PADDING.right;
  const innerHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const xFor = (index) =>
    PADDING.left + (points.length > 1 ? (index / (points.length - 1)) * innerWidth : innerWidth / 2);
  const yFor = (value) =>
    PADDING.top + innerHeight - ((Math.log10(value) - minLog) / logRange) * innerHeight;
  const firstDate = points[0]?.date;
  const middleDate = points[Math.floor((points.length - 1) / 2)]?.date;
  const lastDate = points[points.length - 1]?.date;
  const chartLabel = t("realEstate.casaMapa.marketIntelligence.trend.chartAria");

  return (
    <div className="cm-market-trend__chart" role="img" aria-label={chartLabel}>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="cm-market-trend__svg">
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = PADDING.top + innerHeight * ratio;
          return (
            <line
              key={ratio}
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={y}
              y2={y}
              className="cm-market-trend__gridline"
            />
          );
        })}
        {availableSeries.map((series) => {
          const coordinates = series.values.map((point) => ({
            ...point,
            x: xFor(point.index),
            y: yFor(point.value),
          }));
          return (
            <g key={series.key}>
              {coordinates.length > 1 ? (
                <polyline
                  points={coordinates.map((point) => `${point.x},${point.y}`).join(" ")}
                  fill="none"
                  stroke={series.color}
                  strokeWidth="3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ) : null}
              {coordinates.map((point) => (
                <circle
                  key={`${series.key}-${point.date}`}
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  fill={series.color}
                >
                  <title>
                    {t(`realEstate.casaMapa.marketIntelligence.trend.series.${series.key}`)}
                    {`: ${formatMoney(point.value)} · ${formatDate(point.date, locale)}`}
                  </title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      <div className="cm-market-trend__axis" aria-hidden="true">
        <span>{formatDate(firstDate, locale)}</span>
        <span>{formatDate(middleDate, locale)}</span>
        <span>{formatDate(lastDate, locale)}</span>
      </div>
      <div className="cm-market-trend__legend" aria-hidden="true">
        {SERIES.map((series) => (
          <span key={series.key}>
            <i style={{ backgroundColor: series.color }} />
            {t(`realEstate.casaMapa.marketIntelligence.trend.series.${series.key}`)}
          </span>
        ))}
      </div>
      <table className="cm-sr-only">
        <caption>{chartLabel}</caption>
        <thead>
          <tr>
            <th>{t("realEstate.casaMapa.marketIntelligence.trend.date")}</th>
            {SERIES.map((series) => (
              <th key={series.key}>
                {t(`realEstate.casaMapa.marketIntelligence.trend.series.${series.key}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {points.map((point) => (
            <tr key={point.date}>
              <td>{formatDate(point.date, locale)}</td>
              {SERIES.map((series) => (
                <td key={series.key}>
                  {validAmount(point[series.key]) ? formatMoney(point[series.key]) : "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
